import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import BookingHistoryTable from "../../UserDashboard/SharedComponents/BookingHistoryTable";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import SetupContainer from "../../../setupPage/SharedComponents/SetupContainer";
import Box from "../../../setupPage/SharedComponents/Box";
import useParcelBookingRefresh from "../../../../hooks/useParcelBookingRefresh";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** GetParcelHistory can cartesian-join payments onto product lines — collapse identical rows. */
const detailSignature = (row = {}) =>
  [
    row.parcelCreationDate,
    row.routeName,
    row.cargoTypeName,
    row.shippingServiceName,
    row.weightDescription,
    row.dimensionName,
    row.dimensionCost,
    row.uniqItemDescription,
    row.parcelAdditionalInfo,
    row.paymentStatus,
    row.subTotal,
    row.fixedCommisionAmount,
    row.agentCommisionPayable,
    row.agentName,
  ]
    .map((v) => String(v ?? "").trim().toLowerCase())
    .join("|");

const dedupeParcelDetails = (details = []) => {
  if (!Array.isArray(details) || details.length <= 1) return details || [];
  const seen = new Set();
  const unique = [];
  details.forEach((row) => {
    const key = detailSignature(row);
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(row);
  });
  return unique;
};

const normalizeParcelHistory = (parcels = []) =>
  (Array.isArray(parcels) ? parcels : []).map((parcel) => ({
    ...parcel,
    details: dedupeParcelDetails(parcel?.details),
  }));

const sumParcelSubtotal = (parcel) =>
  (parcel.details || []).reduce(
    (sum, d) => sum + (Number(d.subTotal) || 0),
    0,
  );

const sanitizePaymentStatusLabel = (name) =>
  String(name ?? "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();

const isValidPaymentStatusName = (name) => {
  const label = sanitizePaymentStatusLabel(name);
  if (!label) return false;
  const lower = label.toLowerCase();
  return lower !== "null" && lower !== "undefined";
};

const normalizePaymentStatusKey = (name) =>
  sanitizePaymentStatusLabel(name).toLowerCase();

const getDetailPaymentStatus = (row) => {
  if (!row || typeof row !== "object") return "";
  const raw = row.paymentStatus ?? row.paymentStatusName ?? "";
  return isValidPaymentStatusName(raw) ? sanitizePaymentStatusLabel(raw) : "";
};

/** Parcel-level overall status from GetParcelHistory — not a single invoice. */
const getParcelOverallPaymentStatus = (parcel) => {
  const parcelLevel = getDetailPaymentStatus(parcel);
  if (parcelLevel) return parcelLevel;
  const details = Array.isArray(parcel?.details) ? parcel.details : [];
  return getDetailPaymentStatus(details[0]);
};

const getParcelPaymentStatuses = (parcel) => {
  const overall = getParcelOverallPaymentStatus(parcel);
  return overall ? [overall] : [];
};

const flattenParcelsForHistoryTable = (parcels = []) =>
  (Array.isArray(parcels) ? parcels : []).map((parcel) => {
    const details = Array.isArray(parcel.details) ? parcel.details : [];
    const first = details[0] || {};
    const paymentStatus = getParcelOverallPaymentStatus(parcel);
    return {
      ...first,
      ...parcel,
      parcelNo: parcel.parcelNo,
      senderName: parcel.senderName || first.senderName,
      senderEmail: parcel.senderEmail || first.senderEmail,
      senderPhone: parcel.senderPhone || first.senderPhone,
      receiverName: parcel.receiverName || first.receiverName,
      receiverEmail: parcel.receiverEmail || first.receiverEmail,
      receiverPhone: parcel.receiverPhone || first.receiverPhone,
      routeName: first.routeName || parcel.routeName,
      cargoTypeName: first.cargoTypeName || parcel.cargoTypeName,
      weightDescription: first.weightDescription,
      dimensionName: first.dimensionName,
      subTotal:
        parcel.currentInvoiceAmount ??
        (sumParcelSubtotal(parcel) || first.subTotal),
      symbol: first.symbol || parcel.symbol || "GBP",
      paymentStatus,
      statusName: paymentStatus,
      currentParcelStatusName:
        parcel.currentParcelStatusName || parcel.parcelStatusName,
      parcelCreationDate:
        first.parcelCreationDate || parcel.parcelCreationDate,
    };
  });

const resolvePaymentStatusFilterKey = (filter, statusOptions = []) => {
  if (!filter || filter === "all") return "";

  if (filter.startsWith("name:")) {
    return filter.slice(5);
  }

  const asId = parseInt(filter, 10);
  if (Number.isFinite(asId) && String(asId) === String(filter).trim()) {
    const fromApi = statusOptions.find(
      (s) => parseInt(s.id, 10) === asId,
    );
    if (fromApi?.statusName) {
      return normalizePaymentStatusKey(fromApi.statusName);
    }
  }

  return normalizePaymentStatusKey(filter);
};

const parcelMatchesPaymentStatus = (
  parcel,
  paymentStatusFilter,
  statusOptions = [],
) => {
  const filterKey = resolvePaymentStatusFilterKey(
    paymentStatusFilter,
    statusOptions,
  );
  if (!filterKey) return true;
  return (
    normalizePaymentStatusKey(getParcelOverallPaymentStatus(parcel)) ===
    filterKey
  );
};

const parcelMatchesSearch = (parcel, term) => {
  if (!term) return true;
  const t = term.toLowerCase();
  const parentValues = [
    parcel.parcelNo,
    parcel.parcelStatusName,
    parcel.senderName,
    parcel.senderEmail,
    parcel.senderPhone,
    parcel.senderPostCode,
    parcel.receiverName,
    parcel.receiverEmail,
    parcel.receiverPhone,
    parcel.receiverPostCode,
    parcel.paymentStatus,
    parcel.latestInvoiceNo,
  ];
  if (parentValues.some((v) => v != null && String(v).toLowerCase().includes(t))) {
    return true;
  }
  return (parcel.details || []).some((d) =>
    Object.values(d).some(
      (v) => v != null && String(v).toLowerCase().includes(t),
    ),
  );
};

const AdminBookings = () => {
  const backendUrl = process.env.REACT_APP_API_URL;
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [parcelStatusData, setParcelStatusData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchParcelHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${backendUrl}ParcelBookingInformation/GetParcelHistory`,
        { headers: { accept: "*/*", ...getAuthHeaders() } },
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setParcels(normalizeParcelHistory(data));
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load booking history.";
      toast.error(message);
      setParcels([]);
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchParcelHistory();
  }, [fetchParcelHistory]);

  useParcelBookingRefresh(fetchParcelHistory);

  useEffect(() => {
    axios
      .get(`${backendUrl}Payment/GetAllPaymentStatus`, {
        headers: { accept: "*/*", ...getAuthHeaders() },
      })
      .then((res) => {
        setPaymentStatusOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setPaymentStatusOptions([]));
  }, [backendUrl]);

  const paymentStatusFilterOptions = useMemo(() => {
    const byName = new Map();

    const addOption = (name) => {
      if (!isValidPaymentStatusName(name)) return;
      const label = sanitizePaymentStatusLabel(name);
      const key = normalizePaymentStatusKey(label);
      if (!key || byName.has(key)) return;
      byName.set(key, { value: key, label });
    };

    paymentStatusOptions.forEach((status) => {
      addOption(status.statusName ?? status.name);
    });

    parcels.forEach((parcel) => {
      getParcelPaymentStatuses(parcel).forEach((name) => addOption(name));
    });

    return Array.from(byName.values())
      .filter((opt) => opt.value && opt.label)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [paymentStatusOptions, parcels]);

  useEffect(() => {
    if (paymentStatusFilter === "all") return;
    const isValid = paymentStatusFilterOptions.some(
      (opt) => opt.value === paymentStatusFilter,
    );
    if (!isValid) setPaymentStatusFilter("all");
  }, [paymentStatusFilterOptions, paymentStatusFilter]);

  const filteredParcels = useMemo(
    () =>
      parcels.filter(
        (p) =>
          parcelMatchesSearch(p, searchText.trim()) &&
          parcelMatchesPaymentStatus(
            p,
            paymentStatusFilter,
            paymentStatusOptions,
          ),
      ),
    [parcels, searchText, paymentStatusFilter, paymentStatusOptions],
  );

  const historyRows = useMemo(
    () => flattenParcelsForHistoryTable(filteredParcels),
    [filteredParcels],
  );

  useEffect(() => {
    if (!selectedParcel) {
      setParcelStatusData(null);
      setStatusLoading(false);
      return;
    }
    setStatusLoading(true);
    axios
      .get(
        `${backendUrl}ParcelStatus/GetParcelStatusRankByParcelId?parcelId=${selectedParcel}`,
        { headers: { accept: "*/*", ...getAuthHeaders() } },
      )
      .then((res) => {
        setParcelStatusData(res.data);
        setStatusLoading(false);
      })
      .catch(() => {
        setParcelStatusData(null);
        setStatusLoading(false);
      });
  }, [selectedParcel, backendUrl]);

  const clearFilters = () => {
    setSearchText("");
    setPaymentStatusFilter("all");
  };

  return (
    <SetupContainer>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-600">
        Operations
      </p>
      <SetupTittle text="Bookings" />

      <Box top>
        <div className="rounded-2xl border border-stone-200 bg-white p-4 sm:p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <p className="text-stone-600">
            Expand a row for tracking, parcel details, and payment history.
          </p>
          <button
            type="button"
            onClick={fetchParcelHistory}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-[#1c1917] text-white font-medium hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-3 mb-6">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search parcel no, names, emails, route, status..."
            className="flex-1 border border-stone-200 h-11 px-4 outline-none rounded-xl bg-stone-50 focus:bg-white focus:border-stone-400"
          />
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="border border-stone-200 h-11 px-4 outline-none rounded-xl bg-stone-50 focus:bg-white focus:border-stone-400 min-w-[220px] font-medium text-stone-800"
            aria-label="Filter by payment status"
          >
            <option value="all">All payment statuses</option>
            {paymentStatusFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!searchText && paymentStatusFilter === "all"}
            className="px-5 py-2 rounded-lg border border-gray-300 bg-white font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Clear filters
          </button>
        </div>

        {loading && historyRows.length === 0 ? (
          <p className="text-center py-12 font-medium text-gray-600">
            Loading bookings...
          </p>
        ) : historyRows.length === 0 ? (
          <p className="text-center py-12 font-medium text-gray-600">
            No bookings found.
          </p>
        ) : (
          <BookingHistoryTable
            data={historyRows}
            onSelectParcel={(parcel) => {
              setSelectedParcel(parcel?.parcelNo ?? null);
            }}
            selectedParcel={selectedParcel}
            parcelStatusData={parcelStatusData}
            statusLoading={statusLoading}
            embedded
            showRefund={false}
            showAmendmentDetails
            showCarrierPartner
          />
        )}
        </div>
      </Box>
    </SetupContainer>
  );
};

export default AdminBookings;
